import { Injectable, signal, inject, Injector, runInInjectionContext } from '@angular/core';
import {
  Firestore,
  doc,
  getDoc,
  updateDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
} from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { Membership } from '../models/firestore.types';

export interface AppContext {
  type: 'team' | 'league' | 'organization';
  id: string;
  name: string;
}

@Injectable({
  providedIn: 'root',
})
export class ContextService {
  private injector = inject(Injector);
  private firestore = inject(Firestore);
  private auth = inject(Auth);

  currentContext = signal<AppContext | null>(null);
  availableContexts = signal<AppContext[]>([]);
  private sessionReady = false;

  isSessionReady(): boolean {
    return this.sessionReady;
  }

  markSessionReady(): void {
    this.sessionReady = true;
  }

  constructor() {
    this.loadFromLocalStorage();
  }

  async loadUserContexts(): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) return;

    try {
      const contexts = await runInInjectionContext(this.injector, async () => {
        const membershipsRef = collection(this.firestore, 'memberships');
        const q = query(membershipsRef, where('userId', '==', user.uid));
        const snapshot = await getDocs(q);

        const out: AppContext[] = [];
        const seenEntities = new Set<string>();

        for (const docSnap of snapshot.docs) {
          const membership = docSnap.data() as Membership;
          const entityKey = `${membership.entityType}-${membership.entityId}`;

          if (seenEntities.has(entityKey)) {
            continue;
          }

          const entityDoc = await getDoc(
            doc(this.firestore, `${membership.entityType}s`, membership.entityId)
          );
          if (entityDoc.exists()) {
            out.push({
              type: membership.entityType,
              id: membership.entityId,
              name: entityDoc.data()['name'],
            });
            seenEntities.add(entityKey);
          }
        }
        return out;
      });

      this.availableContexts.set(contexts);

      if (contexts.length > 0 && !this.currentContext()) {
        await this.setContext(contexts[0]);
      }
    } catch (error) {
      console.error('ContextService.loadUserContexts failed:', error);
      this.availableContexts.set([]);
      // Keep currentContext from localStorage if any; auth guard will still allow access
    }
  }

  async setContext(context: AppContext) {
    this.currentContext.set(context);
    localStorage.setItem('currentContext', JSON.stringify(context));

    const user = this.auth.currentUser;
    if (user) {
      await runInInjectionContext(this.injector, async () => {
        const userRef = doc(this.firestore, 'users', user.uid);
        await setDoc(
          userRef,
          {
            currentContext: { type: context.type, id: context.id },
          },
          { merge: true }
        );
      });
    }
  }

  async refreshCurrentContext() {
    const current = this.currentContext();
    if (!current) return;

    await runInInjectionContext(this.injector, async () => {
      const entityDoc = await getDoc(
        doc(this.firestore, `${current.type}s`, current.id)
      );
      if (entityDoc.exists()) {
        const updated = {
          type: current.type,
          id: current.id,
          name: entityDoc.data()['name'],
        };
        this.currentContext.set(updated);
        localStorage.setItem('currentContext', JSON.stringify(updated));
      }
    });
  }

  private loadFromLocalStorage(): void {
    try {
      const stored = localStorage.getItem('currentContext');
      if (!stored) return;

      const parsed = JSON.parse(stored) as unknown;
      if (
        parsed &&
        typeof parsed === 'object' &&
        'type' in parsed &&
        'id' in parsed &&
        'name' in parsed &&
        ['team', 'league', 'organization'].includes((parsed as AppContext).type)
      ) {
        this.currentContext.set(parsed as AppContext);
      } else {
        localStorage.removeItem('currentContext');
      }
    } catch {
      localStorage.removeItem('currentContext');
    }
  }

  clearContext() {
    this.currentContext.set(null);
    this.availableContexts.set([]);
    this.sessionReady = false;
    localStorage.removeItem('currentContext');
  }
}
